"use client";

import { RefreshCw, Shield } from "lucide-react";

import { Field, FieldLayout } from "@/src/components/cards/common";
import { CopyableContent } from "@/src/components/copyable-content";
import { Button } from "@/src/components/shadcn/ui/button";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { getEligibilityPresentation } from "@/src/lib/clearinghouse/stedi/eligibility-presentation";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";

import type { ReactNode } from "react";

export function RecheckButton({
  onRecheck,
  isRechecking,
  label,
  pendingLabel = "Checking...",
}: {
  onRecheck: () => void;
  isRechecking: boolean;
  label: string;
  pendingLabel?: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="flex h-7 items-center gap-1.5 px-2 text-xs text-foreground"
      disabled={isRechecking}
      onClick={onRecheck}
    >
      <RefreshCw className={`h-3 w-3 ${isRechecking ? "animate-spin" : ""}`} />
      {isRechecking ? pendingLabel : label}
    </Button>
  );
}

function formatCheckedAt(createdAt: string) {
  return new Date(createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function StediEligibilityActions({
  check,
  onRecheck,
  isRechecking = false,
}: {
  check: StediEligibilityCheck | null;
  onRecheck?: () => void;
  isRechecking?: boolean;
}) {
  if (!check) {
    return onRecheck ? (
      <RecheckButton onRecheck={onRecheck} isRechecking={isRechecking} label="Check eligibility" />
    ) : null;
  }

  return (
    <>
      <span className="text-xs font-normal">Checked {formatCheckedAt(check.createdAt)}</span>
      {onRecheck && <RecheckButton onRecheck={onRecheck} isRechecking={isRechecking} label="Re-check" />}
    </>
  );
}

const ELIGIBILITY_FIELDS: Field[] = [
  { key: "status", label: "Status" },
  { key: "authorization", label: "Authorization", colSpan: 2 },
  { key: "failureReason", label: "Failure Reason", colSpan: 3 },
  { key: "payerName", label: "Payer", colSpan: 3 },
  { key: "routingPayerId", label: "Eligibility Routing ID", colSpan: 3 },
  { key: "benefitRelatedPayerIds", label: "Benefit Payer ID", colSpan: 3 },
  {
    key: "utilizationManagementOrganizations",
    label: "Utilization Management Organization",
    colSpan: 3,
  },
  { key: "planType", label: "Plan Type", colSpan: 3 },
  { key: "planName", label: "Plan Name", colSpan: 3 },
  { key: "inNetwork", label: "Network" },
];

function StediEligibilityBody({ check }: { check: StediEligibilityCheck | null }) {
  if (!check) {
    return (
      <p className="text-sm text-muted-foreground">
        An automated clearinghouse eligibility check has not been run or is currently pending for this patient. Prior
        authorization requirements may need manual verification.
      </p>
    );
  }

  const display = resolveStediEligibilityDisplay(check);
  const presentation = getEligibilityPresentation(display);
  const checkFailureReason =
    [display.primaryErrorCode, display.primaryErrorDescription].filter(Boolean).join(": ") || null;

  const fields = {
    status: presentation.statusLabel,
    authorization: display.checkFailed
      ? null
      : display.officeVisitRequiresAuth === true
        ? "Requires authorization"
        : display.officeVisitRequiresAuth === false
          ? "Does not require authorization"
          : "Authorization unknown",
    failureReason: display.checkFailed ? checkFailureReason : null,
    payerName: display.checkFailed ? null : display.payerName,
    routingPayerId: display.routingPayerId,
    benefitRelatedPayerIds: display.checkFailed ? null : display.benefitRelatedPayerIds.join(", "),
    utilizationManagementOrganizations: display.checkFailed
      ? null
      : display.utilizationManagementOrganizations.join(", "),
    planType: display.checkFailed ? null : display.planType,
    planName: display.checkFailed ? null : display.planName,
    inNetwork: display.checkFailed
      ? null
      : display.inNetwork === true
        ? "In network"
        : display.inNetwork === false
          ? "Out of network"
          : null,
  };

  const pcps = display.pcps;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLayout fields={ELIGIBILITY_FIELDS} patient={fields} />
        {display.checkFailed ? (
          <p className="text-sm text-muted-foreground">
            No coverage determination was made. Re-check eligibility or verify coverage manually.
          </p>
        ) : (
          <div className="text-sm">
            {pcps.length === 0 ? (
              <div>
                <span className="font-medium">PCP:</span> <CopyableContent text="Not supplied" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <span className="font-medium">{pcps.length === 1 ? "PCP:" : "PCPs:"}</span>
                <div className="space-y-2 pl-2">
                  {pcps.map((p) => (
                    <div
                      key={`${p.name ?? ""}|${p.npi ?? ""}|${p.dates ?? ""}`}
                      className="space-y-0.5 border-l-2 border-muted pl-2 text-xs"
                    >
                      <div>
                        <CopyableContent text={p.name || "Unknown Provider"} />
                        {p.dates && (
                          <span className="ml-1.5 text-muted-foreground">
                            ({p.dates}
                            {p.isActive ? "" : " • Expired"})
                          </span>
                        )}
                      </div>
                      {p.npi && (
                        <div className="text-muted-foreground">
                          NPI: <CopyableContent text={p.npi} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {!display.checkFailed && display.officeVisitAuthReason && (
        <div className="text-sm">
          <div className="font-medium">Authorization Decision:</div>
          <p className="mt-1 text-muted-foreground">{display.officeVisitAuthReason}</p>
        </div>
      )}
    </div>
  );
}

export function StediEligibilitySummary({
  check,
  onRecheck,
  isRechecking = false,
  showHeader = true,
}: {
  check: StediEligibilityCheck | null;
  onRecheck?: () => void;
  isRechecking?: boolean;
  showHeader?: boolean;
}) {
  if (!showHeader) {
    return <StediEligibilityBody check={check} />;
  }

  return (
    <div className="mb-6 mt-4">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-foreground">Insurance Eligibility</h3>
        <StediEligibilityActions check={check} onRecheck={onRecheck} isRechecking={isRechecking} />
      </div>
      <StediEligibilityBody check={check} />
    </div>
  );
}

function eligibilityCardVariant(check: StediEligibilityCheck | null): ValerieCardHeaderVariant {
  const display = check ? resolveStediEligibilityDisplay(check) : null;

  return display ? getEligibilityPresentation(display).cardVariant : "outline";
}

export function StediEligibilityCard({
  check,
  actions,
  className,
}: {
  check: StediEligibilityCheck | null;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <ValerieCard
      title="Insurance Eligibility"
      icon={<Shield />}
      className={className}
      variant={eligibilityCardVariant(check)}
      actions={actions}
    >
      <StediEligibilitySummary check={check} showHeader={false} />
    </ValerieCard>
  );
}
